import React, { useState, useEffect, useRef, memo } from 'react'
import { Box, IconButton, Tooltip, Popover, Typography, Slider, Button, Avatar, Chip, CircularProgress, Menu, MenuItem, Divider } from '@mui/material'
import { ArrowLeftIcon } from '@mui/x-date-pickers'
import ArrowRightIcon from '@mui/icons-material/ArrowRight'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import ListIcon from '@mui/icons-material/List'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import HeightIcon from '@mui/icons-material/Height'
import DescriptionIcon from '@mui/icons-material/Description'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import SearchIcon from '@mui/icons-material/Search'
import PersonIcon from '@mui/icons-material/Person'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import PushPinIcon from '@mui/icons-material/PushPin'
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined'
import dayjs from 'dayjs'
import { useScreenSize } from 'src/customHooks/customHooks'
import MomentDatePicker from 'src/mentor/components/PrescriptionMakingPage/inputComponents/diagnosis/components/MomentDatePicker'
import { GenericDisplayFlexColumn } from 'src/mentor/layout/Styles'
import {
  EDVCDateContainer,
  DateNavButton,
  TodayButton,
} from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingsPageStyles'
import { StyledEventsRefreshIcon } from 'src/mentor/components/MentorEventsPage/MentorEventsPageStyles'
import { styled } from '@mui/material/styles'
import { EDVCSessionsCompletedDetailsComponent } from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingComponents'
import SimplePractitionerButton from '../components/SimplePractitionerButton'

// Styled component for the floating bar
const FloatingBarContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isVisible' && prop !== 'portalHeaderHeight',
})(({ theme, isVisible, portalHeaderHeight }) => ({
  position: 'fixed',
  top: portalHeaderHeight || 0,
  left: 0,
  right: 0,
  width: '100%',
  zIndex: 1500,
  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(249, 250, 251, 0.98) 100%)',
  backdropFilter: 'blur(24px) saturate(180%)',
  WebkitBackdropFilter: 'blur(24px) saturate(180%)',
  boxShadow: '0 4px 16px rgba(67, 56, 202, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04), 0 0 0 1px rgba(67, 56, 202, 0.05)',
  borderBottom: '1px solid rgba(67, 56, 202, 0.1)',
  padding: '8px 16px',
  minHeight: '56px',
  overflow: 'hidden',
  opacity: isVisible ? 1 : 0,
  transform: isVisible ? 'translateY(0)' : 'translateY(-100%)',
  pointerEvents: isVisible ? 'auto' : 'none',
  visibility: isVisible ? 'visible' : 'hidden',
  transition: 'opacity 0.3s ease, transform 0.3s ease, visibility 0.3s ease',
  [theme.breakpoints.down('sm')]: {
    padding: '6px 10px',
    minHeight: '48px',
  },
}))

// Practitioner Avatar component
const PractitionerAvatar = memo(({ imageUrl, managerId }) => {
  return (
    <Avatar
      src={imageUrl || undefined}
      sx={{
        width: 32,
        height: 32,
        bgcolor: 'rgba(116, 80, 170, 0.1)',
        color: 'rgba(116, 80, 170, 0.9)',
        border: '2px solid rgba(116, 80, 170, 0.2)',
        flexShrink: 0,
      }}
      imgProps={{
        style: {
          objectFit: 'cover',
          width: '100%',
          height: '100%',
        },
        onError: (e) => {
          // Prevent further error events - MUI Avatar will automatically show fallback
          e.target.onerror = null
          // Remove the src to prevent repeated load attempts
          e.target.src = ''
        },
        loading: 'eager',
        decoding: 'sync',
      }}
    >
      <PersonIcon sx={{ fontSize: 18 }} />
    </Avatar>
  )
}, (prevProps, nextProps) => {
  return (
    prevProps.imageUrl === nextProps.imageUrl &&
    prevProps.managerId === nextProps.managerId
  )
})

PractitionerAvatar.displayName = 'PractitionerAvatar'

// Old PractitionerButton removed - now using SimplePractitionerButton from shared component

export default function CalendarHeaderFloatingBar({
  selectedDate,
  setSelectedDate,
  onPrevClick,
  onNextClick,
  handleGoToToday,
  isToday,
  onOpenBookedSlotsDrawer,
  onOpenPatientSearch,
  onOpenPractitionerDrawer,
  practitionerButtonProps,
  getAndSetBookedSlotsOfSelectedDate,
  bookedSlotsInDayLoading,
  hideRefreshButton = false,
  clinicSchedules,
  mentorProfileData,
  showPractitionerInfo,
  currentTime,
  currentClinic, // Added for clinic name display
  // Slot duration props
  eachSlotDurationInMins,
  setEachSlotDurationInMins,
  initialSlotDuration,
  // Report data props
  includeReportRelatedData,
  setIncludeReportRelatedData,
  hideToggleSwitchForReportRelatedData = false,
  forMentor = false,
  showCalendarHeaderAfterScroll = true,
  setShowCalendarHeaderAfterScroll,
  // Filter pills props
  selectedFilterType,
  setSelectedFilterType,
  bookedSlotsInDay,
  cancelledCount,
  showCancelledOnly,
  setShowCancelledOnly,
  showCancelledChip,
}) {
  const { width } = useScreenSize()
  const isMobile = width < 768
  const selectedDateDayjs = dayjs(selectedDate, 'DD-MM-YYYY')
  const mobileDateButtonLabel = isToday
    ? `Today · ${selectedDateDayjs.format('D MMM')}`
    : `${selectedDateDayjs.format('ddd')} · ${selectedDateDayjs.format('D MMM')}`
  // Initialize visibility based on showCalendarHeaderAfterScroll setting
  // If enabled, show immediately; if disabled, only show after scroll
  const [isVisible, setIsVisible] = useState(showCalendarHeaderAfterScroll)
  const [portalHeaderHeight, setPortalHeaderHeight] = useState(0)
  const scrollTimeoutRef = useRef(null)
  
  // CA Desktop 3-dots menu state
  const [caActionsMenuAnchor, setCaActionsMenuAnchor] = useState(null)
  
  // Slot duration slider state
  const [slotDurationAnchorEl, setSlotDurationAnchorEl] = useState(null)
  const slotDurationPopoverOpen = Boolean(slotDurationAnchorEl)
  const syntheticAnchorRef = useRef(null)
  // Local state for slider value (not applied until user clicks Apply)
  const [localSlotDuration, setLocalSlotDuration] = useState(eachSlotDurationInMins || 30)
  // Flag to track if we just applied a change (to prevent reset on close)
  const justAppliedRef = useRef(false)
  
  // Slider range: 5 minutes to 120 minutes (2 hours)
  const MIN_SLOT_DURATION = 5
  const MAX_SLOT_DURATION = 120
  
  // Initialize local state when popover opens (but not when eachSlotDurationInMins changes after applying)
  useEffect(() => {
    if (slotDurationPopoverOpen && eachSlotDurationInMins !== undefined) {
      // Only reset if we're opening the popover, not if the value changed externally
      if (!justAppliedRef.current) {
        setLocalSlotDuration(eachSlotDurationInMins)
      }
    }
  }, [slotDurationPopoverOpen])
  
  // Reset the flag when popover closes
  useEffect(() => {
    if (!slotDurationPopoverOpen) {
      justAppliedRef.current = false
    }
  }, [slotDurationPopoverOpen])
  
  const handleSlotDurationClick = (event) => {
    // Validate that the anchor element is in the document
    const anchorElement = event?.currentTarget
    if (anchorElement && document.body.contains(anchorElement)) {
      setSlotDurationAnchorEl(anchorElement)
      // Reset local state to current value when opening
      if (eachSlotDurationInMins !== undefined) {
        setLocalSlotDuration(eachSlotDurationInMins)
      }
    } else {
      console.warn('CalendarHeaderFloatingBar: Invalid anchor element for slot duration popover')
    }
  }
  
  const handleSlotDurationClose = () => {
    // Clean up synthetic anchor if it exists
    if (syntheticAnchorRef.current && syntheticAnchorRef.current.parentNode) {
      syntheticAnchorRef.current.parentNode.removeChild(syntheticAnchorRef.current)
      syntheticAnchorRef.current = null
    }
    setSlotDurationAnchorEl(null)
    // Reset local state to current value when closing without applying
    if (!justAppliedRef.current && eachSlotDurationInMins !== undefined) {
      setLocalSlotDuration(eachSlotDurationInMins)
    }
  }
  
  const handleSlotDurationChange = (event, newValue) => {
    // Only update local state, don't apply to background yet
    setLocalSlotDuration(newValue)
  }
  
  const handleApplySlotDuration = () => {
    // Apply the local value to the actual state
    if (setEachSlotDurationInMins && localSlotDuration !== undefined) {
      justAppliedRef.current = true
      setEachSlotDurationInMins(localSlotDuration)
      // Close the popover after a short delay to show the change
      setTimeout(() => {
        setSlotDurationAnchorEl(null)
      }, 300)
    }
  }
  
  const handleResetSlotDuration = () => {
    if (initialSlotDuration !== undefined) {
      setLocalSlotDuration(initialSlotDuration)
    }
  }
  
  const formatDuration = (minutes) => {
    if (minutes < 60) {
      return `${minutes} min`
    } else if (minutes === 60) {
      return '1h'
    } else {
      const hours = Math.floor(minutes / 60)
      const mins = minutes % 60
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
    }
  }

  // Calculate portal header height
  useEffect(() => {
    const calculatePortalHeaderHeight = () => {
      const portalHeader = document.querySelector('[data-portal-header="true"]')
      if (portalHeader) {
        const height = portalHeader.offsetHeight || portalHeader.getBoundingClientRect().height
        setPortalHeaderHeight(height > 0 ? height : 0)
      } else {
        setPortalHeaderHeight(0)
      }
    }

    calculatePortalHeaderHeight()
    window.addEventListener('resize', calculatePortalHeaderHeight)
    return () => window.removeEventListener('resize', calculatePortalHeaderHeight)
  }, [])

  // Listen for toggle events from portal header
  useEffect(() => {
    const handleToggleChange = (event) => {
      const shouldShow = event.detail?.show || false
      
      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }

      if (shouldShow) {
        // Wait for scroll to stop before showing
        scrollTimeoutRef.current = setTimeout(() => {
          setIsVisible(true)
        }, 300)
      } else {
        setIsVisible(false)
      }
    }

    window.addEventListener('show-calendar-header-after-scroll', handleToggleChange)

    return () => {
      window.removeEventListener('show-calendar-header-after-scroll', handleToggleChange)
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
    }
  }, [])

  // Track if drawer is open
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  
  // Listen for drawer open/close events
  useEffect(() => {
    const handleDrawerChange = (event) => {
      setIsDrawerOpen(event.detail?.open || false)
    }
    
    window.addEventListener('drawer-opened', handleDrawerChange)
    
    return () => {
      window.removeEventListener('drawer-opened', handleDrawerChange)
    }
  }, [])

  // Hide during scroll, show after scroll stops if toggle is enabled
  useEffect(() => {
    let scrollTimeout = null

    const handleScroll = () => {
      // Ignore scroll events when drawer is open
      if (isDrawerOpen) {
        return
      }
      
      // Hide immediately during scroll
      setIsVisible(false)

      // Clear existing timeout
      if (scrollTimeout) {
        clearTimeout(scrollTimeout)
      }

      // Check if toggle is enabled after scroll stops
      if (showCalendarHeaderAfterScroll) {
        scrollTimeout = setTimeout(() => {
          setIsVisible(true)
        }, 300)
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
      if (scrollTimeout) {
        clearTimeout(scrollTimeout)
      }
    }
  }, [isDrawerOpen, showCalendarHeaderAfterScroll])

  return (
    <FloatingBarContainer
      isVisible={isVisible}
      portalHeaderHeight={portalHeaderHeight}
      data-calendar-floating-bar="true"
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          gap: { xs: 0.75, sm: 1, md: 1.5 },
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          overflow: 'hidden',
          minWidth: 0,
        }}
      >
        {/* Reorganized Layout for Clinic Assistant - Two Rows */}
        {!forMentor && practitionerButtonProps ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              gap: 1,
              minHeight: '88px',
            }}
          >
            {/* Row 1: Practitioner Button | Date Picker Group */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                gap: { xs: 0.75, sm: 1, md: 1.5 },
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                overflow: 'hidden',
                minWidth: 0,
                minHeight: { xs: 'auto', sm: '44px' },
              }}
            >
              {/* Left: Practitioner Button - Always visible, critical for clinic assistant flow */}
              <Box sx={{ 
                flexShrink: 0, 
                minWidth: 0, 
                maxWidth: { xs: '140px', sm: '180px', md: '220px' },
                width: { xs: 'auto', sm: 'auto' }
              }}>
                <SimplePractitionerButton
                  imageUrl={practitionerButtonProps.imageUrl}
                  managerId={practitionerButtonProps.managerId}
                  buttonText={practitionerButtonProps.buttonText}
                  onClick={onOpenPractitionerDrawer}
                  isActive={practitionerButtonProps.isActive}
                  variant="floating"
                  role={practitionerButtonProps.role}
                />
              </Box>

              {/* Center: Date Picker - Always visible */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                  minWidth: 0,
                  maxWidth: '100%',
                  overflow: 'hidden',
                }}
              >
              <EDVCDateContainer compact={false} style={{ width: 'auto' }}>
                {/* PREVIOUS DAY BUTTON */}
                <DateNavButton
                  onClick={onPrevClick}
                  title="Previous Day"
                  style={isMobile ? { padding: '6px 8px', minWidth: '36px' } : {}}
                >
                  <ArrowLeftIcon />
                  {!isMobile && (
                    <span>
                      {isToday
                        ? 'Yesterday'
                        : dayjs(selectedDate, 'DD-MM-YYYY').subtract(1, 'days').format('D MMM')}
                    </span>
                  )}
                </DateNavButton>

                {/* DATE SELECTOR */}
                <GenericDisplayFlexColumn
                  aic
                  sx={{
                    justifyContent: 'center',
                    gap: 0.5,
                    flex: { xs: 1, sm: 'unset' },
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: isMobile ? 0.5 : 0.75,
                      flex: { xs: 1, sm: 'unset' },
                      minWidth: 0,
                    }}
                  >
                    <MomentDatePicker
                      date={selectedDateDayjs}
                      buttonTextOverride={
                        isMobile
                          ? mobileDateButtonLabel
                          : `${selectedDate}, ${isToday ? 'Today' : selectedDateDayjs.format('ddd')}`
                      }
                      buttonProps={{
                        variant: 'outlined',
                        size: 'small',
                        endIcon: <CalendarMonthIcon />,
                        sx: {
                          background: 'rgba(255, 255, 255, 0.95)',
                          color: 'rgba(67, 56, 202, 0.85)',
                          textTransform: 'none',
                          border: '1px solid rgba(67, 56, 202, 0.25)',
                          fontWeight: 600,
                          fontSize: { xs: '12px', sm: '13px' },
                          padding: { xs: '6px 10px', sm: '6px 12px' },
                          minHeight: { xs: '36px', sm: 'auto' },
                          boxShadow: '0 1px 2px rgba(67, 56, 202, 0.05)',
                          transition: 'all 0.2s ease',
                          flex: { xs: 1, sm: 'unset' },
                          maxWidth: { xs: '150px', sm: 'none' },
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          '&:hover': {
                            borderColor: 'rgba(67, 56, 202, 0.45)',
                            background: 'rgba(67, 56, 202, 0.08)',
                            color: 'rgba(67, 56, 202, 1)',
                            boxShadow: '0 2px 4px rgba(67, 56, 202, 0.12)',
                            transform: 'translateY(-1px)',
                          },
                          '&:active': {
                            transform: 'translateY(0)',
                          },
                        },
                      }}
                      onChange={(nextValue) => setSelectedDate(nextValue?.format('DD-MM-YYYY'))}
                      calendarProps={{
                        minDetail: 'decade',
                        showNeighboringMonth: false,
                        locale: sessionStorage.getItem('languageCode') || 'en',
                      }}
                      clinicSchedules={clinicSchedules}
                      selectedDate={selectedDate}
                    />
                    {!isToday && !isMobile && (
                      <TodayButton
                        onClick={handleGoToToday}
                        title="Go to Today"
                        style={isMobile ? { padding: '8px 12px', minWidth: '44px' } : {}}
                      >
                        <CalendarMonthIcon />
                        {!isMobile && <span>Today</span>}
                      </TodayButton>
                    )}
                  </Box>
                </GenericDisplayFlexColumn>

                {/* NEXT DAY BUTTON */}
                <DateNavButton
                  onClick={onNextClick}
                  title="Next Day"
                  style={isMobile ? { padding: '6px 8px', minWidth: '36px' } : {}}
                >
                  {!isMobile && (
                    <span>
                      {isToday
                        ? 'Tomorrow'
                        : dayjs(selectedDate, 'DD-MM-YYYY').add(1, 'days').format('D MMM')}
                    </span>
                  )}
                  <ArrowRightIcon />
                </DateNavButton>
              </EDVCDateContainer>
              </Box>
            </Box>

            {/* Row 2: View All Booked Slots | Patient Search | Filter Pills | 3-dots Menu */}
            {/* Hide Row 2 on mobile to avoid duplicate buttons with main header */}
            {!isMobile && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  gap: 1,
                  minHeight: '40px',
                }}
              >
              {/* Left: View All Booked Slots - Always visible on header strip */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* View All Booked Slots - Always visible */}
                {onOpenBookedSlotsDrawer && (
                  <Tooltip title="View All Booked Slots">
                    <IconButton
                      size="small"
                      onClick={onOpenBookedSlotsDrawer}
                      sx={{
                        color: 'rgba(67, 56, 202, 0.7)',
                        backgroundColor: 'rgba(67, 56, 202, 0.04)',
                        border: '1px solid rgba(67, 56, 202, 0.15)',
                        padding: '8px',
                        '&:hover': {
                          backgroundColor: 'rgba(67, 56, 202, 0.08)',
                          borderColor: 'rgba(67, 56, 202, 0.3)',
                          color: 'rgba(67, 56, 202, 1)',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <ListIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}

                {/* Patient Search - Visible on header card */}
                {onOpenPatientSearch && (
                  <Tooltip title="Search / Add Patient">
                    {width < 900 ? (
                      // Icon-only when space is limited
                      <IconButton
                        size="small"
                        onClick={onOpenPatientSearch}
                        sx={{
                          color: '#6779e3',
                          backgroundColor: 'rgba(103, 121, 227, 0.1)',
                          border: '1px solid rgba(103, 121, 227, 0.2)',
                          padding: '8px',
                          '&:hover': {
                            backgroundColor: 'rgba(103, 121, 227, 0.2)',
                            borderColor: 'rgba(103, 121, 227, 0.4)',
                            color: '#6779e3',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <SearchIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    ) : (
                      // Full button when space allows
                      <Button
                        size="small"
                        startIcon={<SearchIcon />}
                        onClick={onOpenPatientSearch}
                        sx={{
                          color: '#6779e3',
                          backgroundColor: 'rgba(103, 121, 227, 0.1)',
                          border: '1px solid rgba(103, 121, 227, 0.2)',
                          padding: '6px 12px',
                          fontSize: '13px',
                          fontWeight: 600,
                          textTransform: 'none',
                          borderRadius: '8px',
                          '&:hover': {
                            backgroundColor: 'rgba(103, 121, 227, 0.2)',
                            borderColor: 'rgba(103, 121, 227, 0.4)',
                          },
                          transition: 'all 0.2s ease',
                          '& .MuiButton-startIcon': {
                            marginRight: '6px',
                            '& > *:nth-of-type(1)': {
                              fontSize: '16px',
                            },
                          },
                        }}
                      >
                        Patient Search
                      </Button>
                    )}
                  </Tooltip>
                )}
              </Box>

              {/* Center: Filter Pills - All Patients, In Person, Video Call */}
              {selectedFilterType !== undefined && setSelectedFilterType && bookedSlotsInDay && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                    gap: 0.5,
                    flexWrap: 'nowrap',
                    overflow: 'hidden',
                    minWidth: 0,
                    maxWidth: '100%',
                    px: 1,
                  }}
                >
                  <EDVCSessionsCompletedDetailsComponent
                    noMargins={true}
                    compactUI={true}
                    bookedSlotsInDay={bookedSlotsInDay}
                    cancelledCount={cancelledCount}
                    showCancelledOnly={showCancelledOnly}
                    setShowCancelledOnly={setShowCancelledOnly}
                    showCancelledChip={showCancelledChip}
                    includeReportRelatedData={includeReportRelatedData}
                    setIncludeReportRelatedData={setIncludeReportRelatedData}
                    hideToggleSwitchForReportRelatedData={true}
                    selectedFilterType={selectedFilterType}
                    setSelectedFilterType={setSelectedFilterType}
                    onOpenBookedSlotsDrawer={onOpenBookedSlotsDrawer}
                  />
                </Box>
              )}

              {/* Right: 3-dots Menu for other actions */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title="More Actions">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      setCaActionsMenuAnchor(e.currentTarget)
                    }}
                  sx={{
                    color: 'rgba(67, 56, 202, 0.7)',
                    backgroundColor: 'rgba(67, 56, 202, 0.04)',
                    border: '1px solid rgba(67, 56, 202, 0.15)',
                    padding: '8px',
                    '&:hover': {
                      backgroundColor: 'rgba(67, 56, 202, 0.08)',
                      borderColor: 'rgba(67, 56, 202, 0.3)',
                      color: 'rgba(67, 56, 202, 1)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <MoreVertIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
              </Box>

              {/* Menu for additional actions */}
              <Menu
                anchorEl={caActionsMenuAnchor}
                open={Boolean(caActionsMenuAnchor)}
                onClose={(e, reason) => {
                  if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    setCaActionsMenuAnchor(null)
                  }
                }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                PaperProps={{
                  sx: {
                    minWidth: 220,
                    mt: 1,
                    boxShadow: '0 4px 16px rgba(67, 56, 202, 0.15)',
                    border: '1px solid rgba(67, 56, 202, 0.1)',
                    borderRadius: '12px',
                  },
                }}
                MenuListProps={{
                  onClick: (e) => e.stopPropagation(),
                  onMouseDown: (e) => e.stopPropagation(),
                  onWheel: (e) => e.stopPropagation(),
                  onScroll: (e) => e.stopPropagation(),
                  onTouchMove: (e) => e.stopPropagation(),
                }}
              >
                {/* Refresh Bookings */}
                {!hideRefreshButton && (
                  <MenuItem
                    onClick={() => {
                      getAndSetBookedSlotsOfSelectedDate()
                      setCaActionsMenuAnchor(null)
                    }}
                    disabled={bookedSlotsInDayLoading}
                    sx={{
                      pointerEvents: 'auto',
                      userSelect: 'none',
                      py: 1.5,
                      color: 'rgba(67, 56, 202, 0.9)',
                      fontSize: '14px',
                      fontWeight: 500,
                      '&:hover': {
                        backgroundColor: 'rgba(67, 56, 202, 0.08)',
                        color: 'rgba(67, 56, 202, 1)',
                      },
                      '&.Mui-disabled': {
                        opacity: 0.5,
                        color: 'rgba(67, 56, 202, 0.5)',
                      },
                    }}
                  >
                    {bookedSlotsInDayLoading ? (
                      <CircularProgress size={16} sx={{ mr: 1.5, color: 'rgba(67, 56, 202, 0.7)' }} />
                    ) : (
                      <RestartAltIcon sx={{ mr: 1.5, fontSize: 20, color: 'rgba(67, 56, 202, 0.7)' }} />
                    )}
                    Refresh Calendar
                  </MenuItem>
                )}

                {/* Slot Duration */}
                {eachSlotDurationInMins !== undefined && setEachSlotDurationInMins && (
                  <>
                    <Divider sx={{ my: 0.5, borderColor: 'rgba(67, 56, 202, 0.1)' }} />
                    <MenuItem
                      onClick={(e) => {
                        e.stopPropagation()
                        // Store the menu's anchor (3-dots button) before closing the menu
                        const menuAnchor = caActionsMenuAnchor
                        
                        // Close the menu first
                        setCaActionsMenuAnchor(null)
                        
                        // Use setTimeout to ensure menu is closed before opening popover
                        // Use the menu's anchor (3-dots button) as the popover anchor for stability
                        setTimeout(() => {
                          if (menuAnchor && document.body.contains(menuAnchor)) {
                            setSlotDurationAnchorEl(menuAnchor)
                          } else {
                            // Fallback: create a synthetic anchor at a reasonable position
                            const syntheticAnchor = document.createElement('div')
                            syntheticAnchor.style.position = 'fixed'
                            syntheticAnchor.style.top = '80px'
                            syntheticAnchor.style.right = '20px'
                            syntheticAnchor.style.width = '1px'
                            syntheticAnchor.style.height = '1px'
                            syntheticAnchor.style.zIndex = '9999'
                            syntheticAnchor.style.pointerEvents = 'none'
                            document.body.appendChild(syntheticAnchor)
                            syntheticAnchorRef.current = syntheticAnchor
                            setSlotDurationAnchorEl(syntheticAnchor)
                          }
                          
                          // Reset local state to current value when opening
                          if (eachSlotDurationInMins !== undefined) {
                            setLocalSlotDuration(eachSlotDurationInMins)
                          }
                        }, 100)
                      }}
                      sx={{
                        pointerEvents: 'auto',
                        userSelect: 'none',
                        py: 1.5,
                        color: 'rgba(67, 56, 202, 0.9)',
                        fontSize: '14px',
                        fontWeight: 500,
                        '&:hover': {
                          backgroundColor: 'rgba(67, 56, 202, 0.08)',
                          color: 'rgba(67, 56, 202, 1)',
                        },
                      }}
                    >
                      <HeightIcon sx={{ mr: 1.5, fontSize: 20, color: 'rgba(67, 56, 202, 0.7)' }} />
                      Slot Duration
                    </MenuItem>
                  </>
                )}

                {/* Sticky Header Toggle */}
                <Divider sx={{ my: 0.5, borderColor: 'rgba(67, 56, 202, 0.1)' }} />
                <MenuItem
                  onClick={() => {
                    setShowCalendarHeaderAfterScroll(!showCalendarHeaderAfterScroll)
                    setCaActionsMenuAnchor(null)
                  }}
                  sx={{
                    pointerEvents: 'auto',
                    userSelect: 'none',
                    py: 1.5,
                    color: 'rgba(67, 56, 202, 0.9)',
                    fontSize: '14px',
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: 'rgba(67, 56, 202, 0.08)',
                      color: 'rgba(67, 56, 202, 1)',
                    },
                  }}
                >
                  {showCalendarHeaderAfterScroll ? (
                    <PushPinIcon sx={{ mr: 1.5, fontSize: 20, color: 'rgba(67, 56, 202, 0.7)' }} />
                  ) : (
                    <PushPinOutlinedIcon sx={{ mr: 1.5, fontSize: 20, color: 'rgba(67, 56, 202, 0.7)' }} />
                  )}
                  {showCalendarHeaderAfterScroll ? 'Pin Header' : 'Unpin Header'}
                </MenuItem>
              </Menu>
            </Box>
            )}
          </Box>
        ) : (
          <>
            {/* Mentor Flow: Date Picker Group - Always visible */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1,
                minWidth: 0,
                maxWidth: '100%',
                overflow: 'hidden',
                gap: 1,
              }}
            >
              {/* Clinic Name and Current Time - For mentor flow */}
              {showPractitionerInfo && currentClinic && !isMobile && forMentor && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: '0 0 auto',
                    flexShrink: 0,
                    minWidth: 0,
                    px: 1.5,
                    py: 0.75,
                    gap: 0.25,
                    backgroundColor: 'rgba(67, 56, 202, 0.08)',
                    borderRadius: '8px',
                    border: '1px solid rgba(67, 56, 202, 0.15)',
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: 'rgba(67, 56, 202, 0.85)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '200px',
                    }}
                    title={currentClinic?.title}
                  >
                    {currentClinic?.title}
                  </Box>
                  {currentClinic?.clinicBrandName && (
                    <Box
                      component="span"
                      sx={{
                        fontSize: '11px',
                        fontWeight: 500,
                        color: 'rgba(67, 56, 202, 0.65)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '200px',
                      }}
                      title={currentClinic?.clinicBrandName}
                    >
                      {currentClinic?.clinicBrandName}
                    </Box>
                  )}
                  {currentTime && (
                    <Box
                      component="span"
                      sx={{
                        fontSize: '10px',
                        fontWeight: 500,
                        color: 'rgba(67, 56, 202, 0.55)',
                        fontFamily: 'monospace',
                        letterSpacing: '0.5px',
                      }}
                    >
                      {currentTime}
                    </Box>
                  )}
                </Box>
              )}
              
              {/* Date Picker Group - Always visible for mentor flow */}
              <EDVCDateContainer compact={false} style={{ width: 'auto' }}>
                {/* PREVIOUS DAY BUTTON */}
                <DateNavButton
                  onClick={onPrevClick}
                  title="Previous Day"
                  style={isMobile ? { padding: '6px 8px', minWidth: '36px' } : {}}
                >
                  <ArrowLeftIcon />
                  {!isMobile && (
                    <span>
                      {isToday
                        ? 'Yesterday'
                        : dayjs(selectedDate, 'DD-MM-YYYY').subtract(1, 'days').format('D MMM')}
                    </span>
                  )}
                </DateNavButton>

                {/* DATE SELECTOR */}
                <GenericDisplayFlexColumn
                  aic
                  sx={{
                    justifyContent: 'center',
                    gap: 0.5,
                    flex: { xs: 1, sm: 'unset' },
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: isMobile ? 0.5 : 0.75,
                      flex: { xs: 1, sm: 'unset' },
                      minWidth: 0,
                    }}
                  >
                    <MomentDatePicker
                      date={selectedDateDayjs}
                      buttonTextOverride={
                        isMobile
                          ? mobileDateButtonLabel
                          : `${selectedDate}, ${isToday ? 'Today' : selectedDateDayjs.format('ddd')}`
                      }
                      buttonProps={{
                        variant: 'outlined',
                        size: 'small',
                        endIcon: <CalendarMonthIcon />,
                        sx: {
                          background: 'rgba(255, 255, 255, 0.95)',
                          color: 'rgba(67, 56, 202, 0.85)',
                          textTransform: 'none',
                          border: '1px solid rgba(67, 56, 202, 0.25)',
                          fontWeight: 600,
                          fontSize: { xs: '12px', sm: '13px' },
                          padding: { xs: '6px 10px', sm: '6px 12px' },
                          minHeight: { xs: '36px', sm: 'auto' },
                          boxShadow: '0 1px 2px rgba(67, 56, 202, 0.05)',
                          transition: 'all 0.2s ease',
                          flex: { xs: 1, sm: 'unset' },
                          maxWidth: { xs: '150px', sm: 'none' },
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          '&:hover': {
                            borderColor: 'rgba(67, 56, 202, 0.45)',
                            background: 'rgba(67, 56, 202, 0.08)',
                            color: 'rgba(67, 56, 202, 1)',
                            boxShadow: '0 2px 4px rgba(67, 56, 202, 0.12)',
                            transform: 'translateY(-1px)',
                          },
                          '&:active': {
                            transform: 'translateY(0)',
                          },
                        },
                      }}
                      onChange={(nextValue) => setSelectedDate(nextValue?.format('DD-MM-YYYY'))}
                      calendarProps={{
                        minDetail: 'decade',
                        showNeighboringMonth: false,
                        locale: sessionStorage.getItem('languageCode') || 'en',
                      }}
                      clinicSchedules={clinicSchedules}
                      selectedDate={selectedDate}
                    />
                    {!isToday && !isMobile && (
                      <TodayButton
                        onClick={handleGoToToday}
                        title="Go to Today"
                        style={isMobile ? { padding: '8px 12px', minWidth: '44px' } : {}}
                      >
                        <CalendarMonthIcon />
                        {!isMobile && <span>Today</span>}
                      </TodayButton>
                    )}
                  </Box>
                </GenericDisplayFlexColumn>

                {/* NEXT DAY BUTTON */}
                <DateNavButton
                  onClick={onNextClick}
                  title="Next Day"
                  style={isMobile ? { padding: '6px 8px', minWidth: '36px' } : {}}
                >
                  {!isMobile && (
                    <span>
                      {isToday
                        ? 'Tomorrow'
                        : dayjs(selectedDate, 'DD-MM-YYYY').add(1, 'days').format('D MMM')}
                    </span>
                  )}
                  <ArrowRightIcon />
                </DateNavButton>
              </EDVCDateContainer>
            </Box>
          </>
        )}

        {/* Action Buttons - Reorganized for Clinic Assistant */}
        {!forMentor && practitionerButtonProps ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              flexShrink: 0,
              minWidth: 0,
              gap: 0.75,
              overflow: 'visible',
            }}
          >
            {/* Patient Search, View All Booked Slots, and Refresh buttons removed from here for CA desktop flow - they're in Row 2 and 3-dots menu */}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              gap: isMobile ? 0.5 : 0.75,
              flexWrap: 'nowrap',
            }}
          >
            {/* 3-dots Menu for Mentor Flow */}
            {forMentor && (
              <Tooltip title="More Actions">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    setCaActionsMenuAnchor(e.currentTarget)
                  }}
                  sx={{
                    color: 'rgba(67, 56, 202, 0.7)',
                    backgroundColor: 'rgba(67, 56, 202, 0.04)',
                    border: '1px solid rgba(67, 56, 202, 0.15)',
                    padding: '8px',
                    '&:hover': {
                      backgroundColor: 'rgba(67, 56, 202, 0.08)',
                      borderColor: 'rgba(67, 56, 202, 0.3)',
                      color: 'rgba(67, 56, 202, 1)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <MoreVertIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}

            {/* Patient Search Button - Icon only on mobile, full button on desktop */}
          {onOpenPatientSearch && (
            isMobile ? (
              <Tooltip title="Search / Add Patient">
                <IconButton
                  size="small"
                  onClick={onOpenPatientSearch}
                  sx={{
                    color: '#6779e3',
                    backgroundColor: 'rgba(103, 121, 227, 0.1)',
                    border: '1px solid rgba(103, 121, 227, 0.2)',
                    padding: '8px',
                    '&:hover': {
                      backgroundColor: 'rgba(103, 121, 227, 0.2)',
                      borderColor: 'rgba(103, 121, 227, 0.4)',
                      color: '#6779e3',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <SearchIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={onOpenPatientSearch}
                sx={{
                  backgroundColor: '#6779e3',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '13px',
                  padding: '8px 16px',
                  borderRadius: '10px',
                  textTransform: 'none',
                  boxShadow: '0 2px 8px rgba(103, 121, 227, 0.25)',
                  border: '1px solid rgba(103, 121, 227, 0.3)',
                  minWidth: '140px',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    backgroundColor: '#7450aa',
                    boxShadow: '0 4px 12px rgba(116, 80, 170, 0.35)',
                    transform: 'translateY(-1px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                  transition: 'all 0.2s ease',
                  '& .MuiButton-startIcon': {
                    marginRight: '6px',
                    '& > *:nth-of-type(1)': {
                      fontSize: '18px',
                    },
                  },
                }}
              >
                Patient Search
              </Button>
            )
          )}
          {/* Hamburger (View All Booked Slots) - Visible on all devices */}
          {onOpenBookedSlotsDrawer && (
            <Tooltip title="View All Booked Slots">
              <IconButton
                size="small"
                onClick={onOpenBookedSlotsDrawer}
                sx={{
                  color: 'rgba(67, 56, 202, 0.7)',
                  backgroundColor: 'rgba(67, 56, 202, 0.04)',
                  border: '1px solid rgba(67, 56, 202, 0.15)',
                  padding: '8px',
                  '&:hover': {
                    backgroundColor: 'rgba(67, 56, 202, 0.08)',
                    borderColor: 'rgba(67, 56, 202, 0.3)',
                    color: 'rgba(67, 56, 202, 1)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <ListIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
          
          {/* Slot Duration Popover - Can be triggered from 3-dots menu */}
          {eachSlotDurationInMins !== undefined && setEachSlotDurationInMins && (
            <Popover
              open={slotDurationPopoverOpen && !!slotDurationAnchorEl && document.body.contains(slotDurationAnchorEl)}
              anchorEl={slotDurationAnchorEl}
              onClose={handleSlotDurationClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              disableRestoreFocus
              disableAutoFocus
              disableEnforceFocus
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              PaperProps={{
                onClick: (e) => e.stopPropagation(),
                onMouseDown: (e) => e.stopPropagation(),
                sx: {
                  mt: 1,
                  p: 2.5,
                  minWidth: { xs: 280, sm: 320 },
                  maxWidth: { xs: 320, sm: 360 },
                  borderRadius: '12px',
                  boxShadow: '0 8px 24px rgba(67, 56, 202, 0.15), 0 2px 8px rgba(0, 0, 0, 0.08)',
                  border: '1px solid rgba(67, 56, 202, 0.1)',
                  position: 'relative', // Ensure stable positioning
                },
              }}
            >
              <Box sx={{ width: '100%' }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: 'rgba(67, 56, 202, 0.9)',
                    mb: 1.5,
                    fontSize: '14px',
                  }}
                >
                  Slot Duration
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: 'rgba(67, 56, 202, 0.7)',
                    mb: 2,
                    fontSize: '16px',
                  }}
                >
                  {formatDuration(localSlotDuration)}
                </Typography>
                <Box 
                  sx={{ px: 1, mb: 2 }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <Slider
                    value={localSlotDuration}
                    onChange={handleSlotDurationChange}
                    min={MIN_SLOT_DURATION}
                    max={MAX_SLOT_DURATION}
                    step={5}
                    marks={[
                      { value: 5, label: '5 min' },
                      { value: 15, label: '15 min' },
                      { value: 30, label: '30 min' },
                      { value: 60, label: '1h' },
                      { value: 90, label: '90m' },
                      { value: 120, label: '2h' },
                    ]}
                    valueLabelDisplay="on"
                    slotProps={{
                      valueLabel: {
                        sx: {
                          '& .MuiSlider-valueLabel': {
                            backgroundColor: 'rgba(67, 56, 202, 0.95)',
                            fontSize: { xs: '12px', sm: '13px' },
                            fontWeight: 600,
                            padding: { xs: '4px 8px', sm: '6px 10px' },
                            borderRadius: '8px',
                            '&::before': {
                              display: 'none',
                            },
                          },
                        },
                      },
                      thumb: {
                        sx: {
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(67, 56, 202, 0.5), 0 0 0 6px rgba(67, 56, 202, 0.15)',
                          },
                          '&:active': {
                            boxShadow: '0 4px 12px rgba(67, 56, 202, 0.5), 0 0 0 6px rgba(67, 56, 202, 0.15)',
                          },
                        },
                      },
                    }}
                    sx={{
                      color: 'rgba(67, 56, 202, 0.9)',
                      height: { xs: 6, sm: 8 },
                      '& .MuiSlider-thumb': {
                        width: { xs: 20, sm: 24 },
                        height: { xs: 20, sm: 24 },
                        backgroundColor: 'rgba(67, 56, 202, 1)',
                        border: '3px solid white',
                        boxShadow: '0 2px 8px rgba(67, 56, 202, 0.4), 0 0 0 4px rgba(67, 56, 202, 0.1)',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(67, 56, 202, 0.5), 0 0 0 6px rgba(67, 56, 202, 0.15)',
                          transform: 'scale(1.1)',
                        },
                        '&:active': {
                          transform: 'scale(1.15)',
                        },
                        '& .MuiSlider-valueLabel': {
                          position: 'absolute !important',
                          top: { xs: '-42px !important', sm: '-48px !important' },
                          left: '50% !important',
                          transform: 'translateX(-50%) !important',
                          pointerEvents: 'none',
                          zIndex: 1,
                        },
                      },
                      '& .MuiSlider-valueLabel': {
                        position: 'absolute !important',
                        top: { xs: '-42px !important', sm: '-48px !important' },
                        left: '50% !important',
                        transform: 'translateX(-50%) !important',
                        pointerEvents: 'none',
                        zIndex: 1,
                      },
                      '& .MuiSlider-track': {
                        backgroundColor: 'rgba(67, 56, 202, 0.9)',
                        height: { xs: 6, sm: 8 },
                        border: 'none',
                      },
                      '& .MuiSlider-rail': {
                        backgroundColor: 'rgba(67, 56, 202, 0.15)',
                        height: { xs: 6, sm: 8 },
                      },
                      '& .MuiSlider-mark': {
                        backgroundColor: 'rgba(67, 56, 202, 0.3)',
                        width: { xs: 4, sm: 6 },
                        height: { xs: 4, sm: 6 },
                        borderRadius: '50%',
                      },
                      '& .MuiSlider-markLabel': {
                        fontSize: { xs: '10px', sm: '11px' },
                        fontWeight: 600,
                        color: 'rgba(67, 56, 202, 0.7)',
                        marginTop: { xs: '8px', sm: '10px' },
                      },
                    }}
                    valueLabelFormat={(value) => formatDuration(value)}
                  />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1.5,
                    mt: 2,
                    pt: 2,
                    borderTop: '1px solid rgba(67, 56, 202, 0.1)',
                  }}
                >
                  <Button
                    onClick={handleResetSlotDuration}
                    disabled={!initialSlotDuration || localSlotDuration === initialSlotDuration}
                    sx={{
                      textTransform: 'none',
                      fontSize: { xs: '13px', sm: '14px' },
                      fontWeight: 600,
                      color: 'rgba(67, 56, 202, 0.8)',
                      backgroundColor: 'rgba(67, 56, 202, 0.06)',
                      border: '1px solid rgba(67, 56, 202, 0.2)',
                      padding: { xs: '4px 12px', sm: '6px 16px' },
                      borderRadius: '8px',
                      '&:hover': {
                        backgroundColor: 'rgba(67, 56, 202, 0.12)',
                        borderColor: 'rgba(67, 56, 202, 0.35)',
                        color: 'rgba(67, 56, 202, 1)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(67, 56, 202, 0.2)',
                      },
                      '&:active': {
                        transform: 'translateY(0)',
                      },
                      '&.Mui-disabled': {
                        color: 'rgba(0, 0, 0, 0.26)',
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        borderColor: 'rgba(0, 0, 0, 0.12)',
                      },
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    Reset
                  </Button>
                  <Box sx={{ flex: 1 }} />
                  <Button
                    onClick={handleApplySlotDuration}
                    variant="contained"
                    startIcon={<CheckCircleIcon sx={{ fontSize: 18 }} />}
                    sx={{
                      textTransform: 'none',
                      fontSize: { xs: '13px', sm: '14px' },
                      fontWeight: 600,
                      background: 'linear-gradient(135deg, rgba(67, 56, 202, 1) 0%, rgba(99, 102, 241, 1) 100%)',
                      color: 'white',
                      padding: { xs: '8px 20px', sm: '10px 24px' },
                      boxShadow: '0 4px 12px rgba(67, 56, 202, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.95) 0%, rgba(99, 102, 241, 0.95) 100%)',
                        boxShadow: '0 6px 16px rgba(67, 56, 202, 0.4)',
                        transform: 'translateY(-1px)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    Apply
                  </Button>
                </Box>
              </Box>
            </Popover>
          )}
          
          {/* Show Reports Data Button - Only for clinic assistant */}
          {!hideToggleSwitchForReportRelatedData && !forMentor && includeReportRelatedData !== undefined && setIncludeReportRelatedData && (
            <Tooltip title={includeReportRelatedData ? 'Hide Reports Data' : 'Show Reports Data'}>
              <IconButton
                size="small"
                onClick={() => setIncludeReportRelatedData((prev) => !prev)}
                sx={{
                  color: includeReportRelatedData 
                    ? 'rgba(67, 56, 202, 1)' 
                    : 'rgba(67, 56, 202, 0.6)',
                  backgroundColor: includeReportRelatedData
                    ? 'rgba(67, 56, 202, 0.12)'
                    : 'rgba(67, 56, 202, 0.04)',
                  border: includeReportRelatedData
                    ? '1px solid rgba(67, 56, 202, 0.3)'
                    : '1px solid rgba(67, 56, 202, 0.15)',
                  padding: '6px',
                  '&:hover': {
                    backgroundColor: includeReportRelatedData
                      ? 'rgba(67, 56, 202, 0.16)'
                      : 'rgba(67, 56, 202, 0.08)',
                    borderColor: 'rgba(67, 56, 202, 0.4)',
                    color: 'rgba(67, 56, 202, 1)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                {includeReportRelatedData ? (
                  <CheckCircleIcon sx={{ fontSize: 18 }} />
                ) : (
                  <DescriptionIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>
        )}

        {/* Filter Pills - Inline with other elements for mentor flow */}
        {forMentor && selectedFilterType !== undefined && setSelectedFilterType && bookedSlotsInDay && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              gap: 0.5,
              flexWrap: 'nowrap',
              overflow: 'hidden',
              minWidth: 0,
              maxWidth: { xs: '100%', sm: 'auto' },
            }}
          >
            <EDVCSessionsCompletedDetailsComponent
              noMargins={true}
              compactUI={true}
              bookedSlotsInDay={bookedSlotsInDay}
              cancelledCount={cancelledCount}
              showCancelledOnly={showCancelledOnly}
              setShowCancelledOnly={setShowCancelledOnly}
              showCancelledChip={showCancelledChip}
              includeReportRelatedData={includeReportRelatedData}
              setIncludeReportRelatedData={setIncludeReportRelatedData}
              hideToggleSwitchForReportRelatedData={true}
              selectedFilterType={selectedFilterType}
              setSelectedFilterType={setSelectedFilterType}
              onOpenBookedSlotsDrawer={onOpenBookedSlotsDrawer}
            />
          </Box>
        )}

        {/* Menu for additional actions - Mentor Flow */}
        {forMentor && (
          <Menu
            anchorEl={caActionsMenuAnchor}
            open={Boolean(caActionsMenuAnchor)}
            onClose={(e, reason) => {
              if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                setCaActionsMenuAnchor(null)
              }
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              sx: {
                minWidth: 220,
                mt: 1,
                boxShadow: '0 4px 16px rgba(67, 56, 202, 0.15)',
                border: '1px solid rgba(67, 56, 202, 0.1)',
                borderRadius: '12px',
              },
            }}
            MenuListProps={{
              onClick: (e) => e.stopPropagation(),
              onMouseDown: (e) => e.stopPropagation(),
              onWheel: (e) => e.stopPropagation(),
              onScroll: (e) => e.stopPropagation(),
              onTouchMove: (e) => e.stopPropagation(),
            }}
          >
            {/* Refresh Bookings */}
            {!hideRefreshButton && (
              <MenuItem
                onClick={() => {
                  getAndSetBookedSlotsOfSelectedDate()
                  setCaActionsMenuAnchor(null)
                }}
                disabled={bookedSlotsInDayLoading}
                sx={{
                  pointerEvents: 'auto',
                  userSelect: 'none',
                  py: 1.5,
                  color: 'rgba(67, 56, 202, 0.9)',
                  fontSize: '14px',
                  fontWeight: 500,
                  '&:hover': {
                    backgroundColor: 'rgba(67, 56, 202, 0.08)',
                    color: 'rgba(67, 56, 202, 1)',
                  },
                  '&.Mui-disabled': {
                    opacity: 0.5,
                    color: 'rgba(67, 56, 202, 0.5)',
                  },
                }}
              >
                {bookedSlotsInDayLoading ? (
                  <CircularProgress size={16} sx={{ mr: 1.5, color: 'rgba(67, 56, 202, 0.7)' }} />
                ) : (
                  <RestartAltIcon sx={{ mr: 1.5, fontSize: 20, color: 'rgba(67, 56, 202, 0.7)' }} />
                )}
                Refresh Calendar
              </MenuItem>
            )}

            {/* Slot Duration */}
            {eachSlotDurationInMins !== undefined && setEachSlotDurationInMins && (
              <>
                <Divider sx={{ my: 0.5, borderColor: 'rgba(67, 56, 202, 0.1)' }} />
                <MenuItem
                  onClick={(e) => {
                    e.stopPropagation()
                    // Store the menu's anchor (3-dots button) before closing the menu
                    const menuAnchor = caActionsMenuAnchor
                    
                    // Close the menu first
                    setCaActionsMenuAnchor(null)
                    
                    // Use setTimeout to ensure menu is closed before opening popover
                    // Use the menu's anchor (3-dots button) as the popover anchor for stability
                    setTimeout(() => {
                      if (menuAnchor && document.body.contains(menuAnchor)) {
                        setSlotDurationAnchorEl(menuAnchor)
                      } else {
                        // Fallback: create a synthetic anchor at a reasonable position
                        const syntheticAnchor = document.createElement('div')
                        syntheticAnchor.style.position = 'fixed'
                        syntheticAnchor.style.top = '80px'
                        syntheticAnchor.style.right = '20px'
                        syntheticAnchor.style.width = '1px'
                        syntheticAnchor.style.height = '1px'
                        syntheticAnchor.style.zIndex = '9999'
                        syntheticAnchor.style.pointerEvents = 'none'
                        document.body.appendChild(syntheticAnchor)
                        syntheticAnchorRef.current = syntheticAnchor
                        setSlotDurationAnchorEl(syntheticAnchor)
                      }
                      
                      // Reset local state to current value when opening
                      if (eachSlotDurationInMins !== undefined) {
                        setLocalSlotDuration(eachSlotDurationInMins)
                      }
                    }, 100)
                  }}
                  sx={{
                    pointerEvents: 'auto',
                    userSelect: 'none',
                    py: 1.5,
                    color: 'rgba(67, 56, 202, 0.9)',
                    fontSize: '14px',
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: 'rgba(67, 56, 202, 0.08)',
                      color: 'rgba(67, 56, 202, 1)',
                    },
                  }}
                >
                  <HeightIcon sx={{ mr: 1.5, fontSize: 20, color: 'rgba(67, 56, 202, 0.7)' }} />
                  Slot Duration
                </MenuItem>
              </>
            )}

            {/* Sticky Header Toggle */}
            <Divider sx={{ my: 0.5, borderColor: 'rgba(67, 56, 202, 0.1)' }} />
            <MenuItem
              onClick={() => {
                setShowCalendarHeaderAfterScroll(!showCalendarHeaderAfterScroll)
                setCaActionsMenuAnchor(null)
              }}
              sx={{
                pointerEvents: 'auto',
                userSelect: 'none',
                py: 1.5,
                color: 'rgba(67, 56, 202, 0.9)',
                fontSize: '14px',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'rgba(67, 56, 202, 0.08)',
                  color: 'rgba(67, 56, 202, 1)',
                },
              }}
            >
              {showCalendarHeaderAfterScroll ? (
                <PushPinIcon sx={{ mr: 1.5, fontSize: 20, color: 'rgba(67, 56, 202, 0.7)' }} />
              ) : (
                <PushPinOutlinedIcon sx={{ mr: 1.5, fontSize: 20, color: 'rgba(67, 56, 202, 0.7)' }} />
              )}
              {showCalendarHeaderAfterScroll ? 'Hide' : 'Show'} Date Picker After Scroll
            </MenuItem>
          </Menu>
        )}
      </Box>
    </FloatingBarContainer>
  )
}
